import { Permission } from '@procureai/permissions';
import { DateFormatPreset } from '@procureai/ui-mui';
import { fromUnixTime } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgSearchPurchaseOrderHeader } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  dateFormatterFactory,
  GridAlignmentType,
  gridColDefFactory,
  LinkCell,
  LinkCellValue,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { RouteId } from '~/routes/RouteId';

export enum PurchaseOrderResultDataGridField {
  PURCHASE_ORDER_REFERENCE = 'purchaseOrderReference',
  SUPPLIER = 'supplier',
  ORDERED_AT_TIMESTAMP = 'orderedAtTimestamp',
  LINE_ITEM_COUNT = 'lineItemCount',
  TOTAL_VALUE = 'totalValue',
}

export const usePurchaseOrderResultDataGrid: UseDataGridConfig<SvbrgSearchPurchaseOrderHeader> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'PurchaseOrderResultDataGrid.column',
  });

  return useMemo(() => {
    const createColumn = gridColDefFactory({
      tColumn,
      t,
      defaultOptions: { sortable: false, align: GridAlignmentType.LEFT, minWidth: 120 },
    });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: PurchaseOrderResultDataGridField.PURCHASE_ORDER_REFERENCE,
          renderCell: cellFactory(LinkCell, { needsPermission: Permission.BaseOrderList }),
          valueGetter: (_value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.orderDetail(row.id),
            label: row.purchaseOrderReference,
          }),
          hideable: false,
        }),
        createColumn({
          field: PurchaseOrderResultDataGridField.SUPPLIER,
          minWidth: 250,
          renderCell: cellFactory(LinkCell, { needs: { permission: Permission.BaseSupplierList } }),
          valueGetter: (_value, row): LinkCellValue =>
            row.supplierName
              ? {
                  variant: DataGridLinkVariant.PRIMARY,
                  to: RouteId.supplierDetail(row.supplierId),
                  label: row.supplierName,
                }
              : null,
        }),
        createColumn({
          field: PurchaseOrderResultDataGridField.ORDERED_AT_TIMESTAMP,
          valueGetter: (_value, row) => fromUnixTime(row.orderedAtTimestamp),
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
        }),
        createColumn({
          field: PurchaseOrderResultDataGridField.LINE_ITEM_COUNT,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: PurchaseOrderResultDataGridField.TOTAL_VALUE,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency, value: row.totalValue })),
        }),
      ],
    } as DataGridConfig<SvbrgSearchPurchaseOrderHeader>;
  }, [tColumn, t]);
};
